/**
 * T-SQL post-processor that runs after `sql-formatter`.
 *
 * Reformats CREATE/ALTER PROC|PROCEDURE|FUNCTION definitions so that:
 *  - the procedure name sits on its own line
 *  - each parameter sits on its own indented line
 *  - AS, BEGIN and END are on their own lines
 *  - DECLARE statements glued to other statements are broken onto their own lines
 *  - the body inside BEGIN/END is indented one level (with proper handling of
 *    nested BEGIN/END, BEGIN TRY/CATCH and CASE/END)
 *
 * Designed to be a no-op for non-procedural SQL and idempotent on already
 * well-formatted output.
 */

const INDENT = '  ';

type CharState = {
  topLevel: boolean;
  inString: boolean;
  inComment: boolean;
};

type StructToken = {
  type:
    | 'case'
    | 'begin-block'
    | 'begin-try'
    | 'begin-catch'
    | 'begin-tran'
    | 'end-block'
    | 'end-try'
    | 'end-catch';
  start: number;
  end: number;
};

function analyze(sql: string): CharState[] {
  const states: CharState[] = new Array(sql.length);
  let parenDepth = 0;
  let bracketDepth = 0;
  let inSQ = false;
  let inDQ = false;
  let inLine = false;
  let inBlock = false;

  for (let i = 0; i < sql.length; i++) {
    const ch = sql[i];
    const next = i + 1 < sql.length ? sql[i + 1] : '';

    if (inLine) {
      states[i] = { topLevel: false, inString: false, inComment: true };
      if (ch === '\n') inLine = false;
      continue;
    }
    if (inBlock) {
      states[i] = { topLevel: false, inString: false, inComment: true };
      if (ch === '*' && next === '/') {
        inBlock = false;
        if (i + 1 < sql.length) {
          states[i + 1] = { topLevel: false, inString: false, inComment: true };
          i++;
        }
      }
      continue;
    }
    if (inSQ) {
      states[i] = { topLevel: false, inString: true, inComment: false };
      if (ch === "'") {
        if (next === "'") {
          if (i + 1 < sql.length) {
            states[i + 1] = { topLevel: false, inString: true, inComment: false };
            i++;
          }
        } else {
          inSQ = false;
        }
      }
      continue;
    }
    if (inDQ) {
      states[i] = { topLevel: false, inString: true, inComment: false };
      if (ch === '"') inDQ = false;
      continue;
    }

    if (ch === '-' && next === '-') {
      states[i] = { topLevel: false, inString: false, inComment: true };
      inLine = true;
      continue;
    }
    if (ch === '/' && next === '*') {
      states[i] = { topLevel: false, inString: false, inComment: true };
      inBlock = true;
      continue;
    }
    if (ch === "'") {
      states[i] = { topLevel: false, inString: true, inComment: false };
      inSQ = true;
      continue;
    }
    if (ch === '"') {
      states[i] = { topLevel: false, inString: true, inComment: false };
      inDQ = true;
      continue;
    }

    if (ch === '(') {
      states[i] = {
        topLevel: parenDepth === 0 && bracketDepth === 0,
        inString: false,
        inComment: false,
      };
      parenDepth++;
      continue;
    }
    if (ch === ')') {
      parenDepth = Math.max(0, parenDepth - 1);
      states[i] = {
        topLevel: parenDepth === 0 && bracketDepth === 0,
        inString: false,
        inComment: false,
      };
      continue;
    }
    if (ch === '[') {
      states[i] = {
        topLevel: parenDepth === 0 && bracketDepth === 0,
        inString: false,
        inComment: false,
      };
      bracketDepth++;
      continue;
    }
    if (ch === ']') {
      bracketDepth = Math.max(0, bracketDepth - 1);
      states[i] = {
        topLevel: parenDepth === 0 && bracketDepth === 0,
        inString: false,
        inComment: false,
      };
      continue;
    }

    states[i] = {
      topLevel: parenDepth === 0 && bracketDepth === 0,
      inString: false,
      inComment: false,
    };
  }

  return states;
}

function isWordChar(ch: string | undefined): boolean {
  return !!ch && /\w/.test(ch);
}

function findKeywordTopLevel(
  sql: string,
  states: CharState[],
  keyword: string,
  fromIndex: number,
): number {
  const lower = sql.toLowerCase();
  const target = keyword.toLowerCase();
  let i = fromIndex;
  while (i <= sql.length - target.length) {
    const idx = lower.indexOf(target, i);
    if (idx === -1) return -1;
    const before = idx > 0 ? sql[idx - 1] : ' ';
    const after = idx + target.length < sql.length ? sql[idx + target.length] : ' ';
    if (isWordChar(before) || isWordChar(after)) {
      i = idx + 1;
      continue;
    }
    const st = states[idx];
    if (!st || !st.topLevel || st.inString || st.inComment) {
      i = idx + 1;
      continue;
    }
    return idx;
  }
  return -1;
}

function splitTopLevelCommas(text: string): string[] {
  if (!text) return [];
  const states = analyze(text);
  const parts: string[] = [];
  let start = 0;
  for (let i = 0; i < text.length; i++) {
    if (text[i] === ',' && states[i] && states[i].topLevel && !states[i].inString && !states[i].inComment) {
      parts.push(text.slice(start, i));
      start = i + 1;
    }
  }
  parts.push(text.slice(start));
  return parts;
}

function findMatchingCloseParen(text: string, openIdx: number): number {
  const states = analyze(text);
  let depth = 0;
  for (let i = openIdx; i < text.length; i++) {
    if (states[i].inString || states[i].inComment) continue;
    const ch = text[i];
    if (ch === '(') depth++;
    else if (ch === ')') {
      depth--;
      if (depth === 0) return i;
    }
  }
  return -1;
}

function tokenizeStructural(sql: string): StructToken[] {
  const states = analyze(sql);
  const tokens: StructToken[] = [];
  const regex = /\b(CASE|BEGIN|END)\b/gi;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(sql)) !== null) {
    const idx = m.index;
    const st = states[idx];
    if (!st || !st.topLevel || st.inString || st.inComment) continue;

    const word = m[1].toUpperCase();
    if (word === 'CASE') {
      tokens.push({ type: 'case', start: idx, end: idx + 4 });
    } else if (word === 'BEGIN') {
      const tail = sql.slice(idx + 5);
      const mod = tail.match(/^\s+(TRY|CATCH|TRAN|TRANSACTION|DISTRIBUTED)\b/i);
      if (mod) {
        const kind = mod[1].toUpperCase();
        const len = 5 + mod[0].length;
        if (kind === 'TRY') tokens.push({ type: 'begin-try', start: idx, end: idx + len });
        else if (kind === 'CATCH') tokens.push({ type: 'begin-catch', start: idx, end: idx + len });
        else tokens.push({ type: 'begin-tran', start: idx, end: idx + len });
      } else {
        tokens.push({ type: 'begin-block', start: idx, end: idx + 5 });
      }
    } else if (word === 'END') {
      const tail = sql.slice(idx + 3);
      const mod = tail.match(/^\s+(TRY|CATCH)\b/i);
      if (mod) {
        const kind = mod[1].toUpperCase();
        const len = 3 + mod[0].length;
        if (kind === 'TRY') tokens.push({ type: 'end-try', start: idx, end: idx + len });
        else tokens.push({ type: 'end-catch', start: idx, end: idx + len });
      } else {
        tokens.push({ type: 'end-block', start: idx, end: idx + 3 });
      }
    }
  }
  return tokens;
}

function findDefinitionStarts(sql: string, states: CharState[]): number[] {
  const starts: number[] = [];
  const regex = /\b(?:CREATE\s+(?:OR\s+ALTER\s+)?|ALTER\s+)(?:PROC(?:EDURE)?|FUNCTION)\b/gi;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(sql)) !== null) {
    const idx = m.index;
    const st = states[idx];
    if (!st || !st.topLevel || st.inString || st.inComment) continue;
    starts.push(idx);
  }
  return starts;
}

function reformatHeader(header: string): string {
  const kindMatch = header.match(
    /^(?:CREATE\s+(?:OR\s+ALTER\s+)?|ALTER\s+)(?:PROC(?:EDURE)?|FUNCTION)\b/i,
  );
  if (!kindMatch) return header.trim();

  const kind = kindMatch[0].replace(/\s+/g, ' ').trim();
  const rest = header.slice(kindMatch[0].length).replace(/^\s+/, '');

  if (rest.length === 0) return kind;

  const restStates = analyze(rest);

  let nameEnd = -1;
  for (let i = 0; i < rest.length; i++) {
    const st = restStates[i];
    if (!st || !st.topLevel || st.inString || st.inComment) continue;
    const ch = rest[i];
    if (ch === '(' || ch === '@') {
      nameEnd = i;
      break;
    }
  }

  let name: string;
  let paramsRaw: string;
  if (nameEnd === -1) {
    name = rest.replace(/\s+/g, ' ').trim();
    paramsRaw = '';
  } else {
    name = rest.slice(0, nameEnd).replace(/\s+$/, '');
    paramsRaw = rest.slice(nameEnd);
  }

  let returnsClause = '';
  paramsRaw = paramsRaw.replace(/^\s+/, '');
  if (paramsRaw.startsWith('(')) {
    const closeIdx = findMatchingCloseParen(paramsRaw, 0);
    if (closeIdx !== -1) {
      const innerParams = paramsRaw.slice(1, closeIdx);
      const afterClose = paramsRaw.slice(closeIdx + 1).trim();
      paramsRaw = innerParams;
      if (afterClose) returnsClause = afterClose.replace(/\s+/g, ' ');
    }
  }

  const params = splitTopLevelCommas(paramsRaw)
    .map((p) => p.replace(/\s+/g, ' ').trim())
    .filter((p) => p.length > 0);

  // Functions require the parameter list to be wrapped in parentheses —
  // `CREATE FUNCTION f (@a INT) RETURNS ...` — while procedures accept the
  // bare, paren-less list the branches below emit. Dropping the parens for a
  // function produces DDL that SQL Server rejects with a syntax error near the
  // first parameter. `kind` is the normalized leading keyword (`CREATE
  // FUNCTION`, `CREATE OR ALTER FUNCTION`, `ALTER FUNCTION`, or a PROC
  // variant), so a trailing-word check tells the two apart.
  const isFunction = /\bFUNCTION$/i.test(kind);

  if (params.length === 0) {
    // A zero-parameter function still needs an empty `()` to stay valid SQL;
    // procedures emit nothing.
    const nameLine = isFunction ? `${kind} ${name}\n()` : `${kind} ${name}`;
    return returnsClause ? `${nameLine}\n${returnsClause}` : nameLine;
  }

  const paramLines = params.map(
    (p, i) => INDENT + p + (i < params.length - 1 ? ',' : ''),
  );
  const paramBlock = paramLines.join('\n');

  const headerCore = isFunction
    ? `${kind} ${name}\n(\n${paramBlock}\n)`
    : `${kind} ${name}\n${paramBlock}`;
  return returnsClause ? `${headerCore}\n${returnsClause}` : headerCore;
}

function findOuterBlockEnd(body: string): number {
  const tokens = tokenizeStructural(body);
  if (tokens.length === 0 || tokens[0].type !== 'begin-block') return -1;
  const stack: StructToken[] = [tokens[0]];
  for (let i = 1; i < tokens.length; i++) {
    const tok = tokens[i];
    if (
      tok.type === 'case' ||
      tok.type === 'begin-block' ||
      tok.type === 'begin-try' ||
      tok.type === 'begin-catch'
    ) {
      stack.push(tok);
    } else if (tok.type === 'end-block') {
      const top = stack.pop();
      if (stack.length === 0 && top && top.type === 'begin-block') {
        return tok.start;
      }
    } else if (tok.type === 'end-try' || tok.type === 'end-catch') {
      stack.pop();
    }
  }
  return -1;
}

function breakOutKeywords(text: string): string {
  const states = analyze(text);
  const breakBefore: number[] = [];
  const regex = /\b(BEGIN|END|DECLARE)\b/gi;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(text)) !== null) {
    const idx = m.index;
    const st = states[idx];
    if (!st || !st.topLevel || st.inString || st.inComment) continue;

    let j = idx - 1;
    while (j >= 0 && (text[j] === ' ' || text[j] === '\t')) j--;
    if (j < 0 || text[j] === '\n') continue;
    breakBefore.push(idx);
  }

  if (breakBefore.length === 0) return text;
  breakBefore.sort((a, b) => b - a);
  let result = text;
  for (const idx of breakBefore) {
    let wsStart = idx;
    while (wsStart > 0 && (result[wsStart - 1] === ' ' || result[wsStart - 1] === '\t')) {
      wsStart--;
    }
    result = result.slice(0, wsStart) + '\n' + result.slice(idx);
  }
  return result;
}

function indentInner(text: string): string {
  const tokens = tokenizeStructural(text);
  const lines = text.split('\n');

  // Idempotency guard: if the body is already indented to at least one level
  // (the first non-empty line starts with at least INDENT.length spaces),
  // assume it has already been processed and pass it through unchanged.
  const firstNonEmpty = lines.find((l) => l.trim() !== '');
  if (firstNonEmpty) {
    const leading = firstNonEmpty.match(/^[ \t]*/);
    const leadingLen = leading ? leading[0].length : 0;
    if (leadingLen >= INDENT.length) return text;
  }

  const lineStarts: number[] = [0];
  for (let i = 0; i < text.length; i++) {
    if (text[i] === '\n') lineStarts.push(i + 1);
  }

  const stackSnapshots: StructToken[][] = [];
  const stack: StructToken[] = [];
  let tokIdx = 0;
  for (let lineNo = 0; lineNo < lines.length; lineNo++) {
    const lineStart = lineStarts[lineNo];
    while (tokIdx < tokens.length && tokens[tokIdx].start < lineStart) {
      const tok = tokens[tokIdx];
      if (
        tok.type === 'case' ||
        tok.type === 'begin-block' ||
        tok.type === 'begin-try' ||
        tok.type === 'begin-catch'
      ) {
        stack.push(tok);
      } else if (
        tok.type === 'end-block' ||
        tok.type === 'end-try' ||
        tok.type === 'end-catch'
      ) {
        stack.pop();
      }
      tokIdx++;
    }
    stackSnapshots.push([...stack]);
  }

  const result: string[] = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.trim() === '') {
      result.push('');
      continue;
    }

    const lineStart = lineStarts[i];
    let nonWs = lineStart;
    while (nonWs < text.length && (text[nonWs] === ' ' || text[nonWs] === '\t')) {
      nonWs++;
    }
    const tokAtStart = tokens.find((t) => t.start === nonWs);
    const snap = stackSnapshots[i];
    const blockDepth = snap.filter((t) => t.type !== 'case').length;

    let isBlockEndLine = false;
    if (tokAtStart) {
      if (tokAtStart.type === 'end-block') {
        const top = snap[snap.length - 1];
        if (
          top &&
          (top.type === 'begin-block' ||
            top.type === 'begin-try' ||
            top.type === 'begin-catch')
        ) {
          isBlockEndLine = true;
        }
      } else if (tokAtStart.type === 'end-try' || tokAtStart.type === 'end-catch') {
        isBlockEndLine = true;
      }
    }

    const depth = isBlockEndLine ? blockDepth : blockDepth + 1;
    result.push(INDENT.repeat(depth) + line);
  }

  return result.join('\n');
}

function reformatBody(body: string): string {
  const trimmed = body.trim();
  if (!trimmed) return trimmed;

  const beginMatch = trimmed.match(/^BEGIN\b/i);
  if (!beginMatch) return trimmed;

  const endIdx = findOuterBlockEnd(trimmed);
  if (endIdx === -1) return trimmed;

  const innerStart = beginMatch[0].length;
  const innerRaw = trimmed.slice(innerStart, endIdx);
  // Preserve existing indentation when the body content begins on a new line
  // (already-formatted second-run input). Otherwise normalize inline content.
  const leadingWsMatch = innerRaw.match(/^[ \t]*/);
  const leadingWsLen = leadingWsMatch ? leadingWsMatch[0].length : 0;
  let inner: string;
  if (innerRaw[leadingWsLen] === '\n') {
    inner = innerRaw.slice(leadingWsLen + 1).replace(/\s+$/, '');
  } else {
    inner = innerRaw.replace(/^\s+/, '').replace(/\s+$/, '');
  }
  const after = trimmed.slice(endIdx + 3).replace(/^\s+/, '');

  if (!inner) {
    return after ? `BEGIN\nEND\n${after}` : 'BEGIN\nEND';
  }

  const broken = breakOutKeywords(inner);
  const indented = indentInner(broken);
  const tail = after ? `\n${after}` : '';
  return `BEGIN\n${indented}\nEND${tail}`;
}

function reformatDefinition(text: string): string {
  const trailingMatch = text.match(/\s*$/);
  const trailing = trailingMatch ? trailingMatch[0] : '';
  const core = text.slice(0, text.length - trailing.length);
  if (!core) return text;

  const states = analyze(core);
  const asIdx = findKeywordTopLevel(core, states, 'AS', 0);
  if (asIdx === -1) return text;

  const header = core.slice(0, asIdx).replace(/\s+$/, '');
  const afterAs = core.slice(asIdx + 2).replace(/^\s+/, '');

  const reformattedHeader = reformatHeader(header);
  const reformattedBody = reformatBody(afterAs);

  return `${reformattedHeader}\nAS\n${reformattedBody}${trailing}`;
}

export function formatTSqlBlocks(sql: string): string {
  if (!sql.trim()) return sql;

  const states = analyze(sql);
  const starts = findDefinitionStarts(sql, states);
  if (starts.length === 0) return sql;

  let result = '';
  let lastEnd = 0;
  for (let i = 0; i < starts.length; i++) {
    const start = starts[i];
    const end = i + 1 < starts.length ? starts[i + 1] : sql.length;
    result += sql.slice(lastEnd, start);
    result += reformatDefinition(sql.slice(start, end));
    lastEnd = end;
  }
  result += sql.slice(lastEnd);
  return result;
}
